import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Breadcrumbs,
  Link,
  Alert,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Home as HomeIcon,
  Restaurant as RestaurantIcon,
  MenuBook as MenuBookIcon,
  ExpandMore as ExpandMoreIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import KenyanContext from './components/KenyanContext';
import KitchenNetworkOverview from './components/KitchenNetworkOverview';
import ProductionMetrics from './components/ProductionMetrics';
import MealSchedule from './components/MealSchedule';
import SupplyChainStatus from './components/SupplyChainStatus';
import KitchenOrders from './components/KitchenOrders';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

const KitchenOps: React.FC = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [stationCount, setStationCount] = useState<string>('--');

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch live kitchen station count
  useEffect(() => {
    const fetchStationCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const resp = await fetch(`${API_BASE}/kitchen-ops/kitchen-stations`, { headers });
        if (!resp.ok) return;
        const data = await resp.json();
        const list = data.data ?? data;
        const count = Array.isArray(list) ? list.length : (data.total ?? data.count ?? null);
        if (count != null) {
          setStationCount(count.toLocaleString());
        }
      } catch {
        // keep default '--'
      }
    };
    fetchStationCount();
  }, []);

  const getCurrentShift = (): string => {
    const hour = currentTime.getHours();
    if (hour >= 7 && hour < 10) return 'Breakfast Service (7:30-8:30 AM)';
    if (hour >= 10 && hour < 12) return 'Mid-Morning Prep (10:00 AM)';
    if (hour >= 12 && hour < 14) return 'Lunch Service (12:30-1:30 PM)';
    if (hour >= 14 && hour < 16) return 'Afternoon Snack (3:00 PM)';
    return 'Prep & Planning';
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <RestaurantIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Kitchen Operations
          </Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
              Kitchen Operations Center
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitoring & Managing {stationCount} School Kitchens Across Kenya
            </Typography>
          </Box>
        </Box>

        {/* Real-time Service Information */}
        <Alert
          severity="info"
          sx={{ mb: 2, backgroundColor: '#e8f5e8', border: '1px solid #c3e6c3' }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Typography component="span">
              <strong>Current Service:</strong> {getCurrentShift()}
            </Typography>
            <Typography component="span">
              <strong>Time:</strong> {currentTime.toLocaleTimeString()}
            </Typography>
          </Box>
        </Alert>
      </Box>

      {/* Kenyan Context Section */}
      <KenyanContext />

      {/* Main Dashboard Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <KitchenNetworkOverview />
        </Grid>
        <Grid item xs={12} lg={6}>
          <ProductionMetrics />
        </Grid>
      </Grid>

      {/* Meal Schedule */}
      <MealSchedule />

      {/* Supply Chain Status */}
      <SupplyChainStatus />

      {/* Kitchen Orders — collapsible section */}
      <Accordion
        defaultExpanded
        sx={{
          mt: 3,
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: 'linear-gradient(135deg, #FF6B35, #F7931E)',
            color: 'white',
            '& .MuiAccordionSummary-expandIconWrapper': { color: 'white' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ListAltIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Kitchen Orders
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <KitchenOrders />
        </AccordionDetails>
      </Accordion>

      {/* Quick Access to Meal Planning */}
      <Card sx={{ mt: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MenuBookIcon sx={{ fontSize: 40, color: '#28a745' }} />
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Meal Planning & Recipes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access nutrition targets, weekly menu planning, seasonal ingredients, recipe database, and cost analysis
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<MenuBookIcon />}
                onClick={() => navigate('/kitchen-ops/meal-planning')}
                sx={{
                  background: 'linear-gradient(135deg, #28a745, #20c997)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #20c997, #17a085)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 5px 15px rgba(40,167,69,0.4)'
                  },
                  transition: 'all 0.3s ease',
                  py: 1.5
                }}
              >
                Open Meal Planning
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default KitchenOps;
